import React, { useState, useEffect, useRef } from 'react';
import {
  AssetStuffingChartDatum,
  AssetStuffingChartProps,
  TopixData,
} from './AssetStuffing';
import {
  ChartForNormalProps,
  ChartForPreviewInternalProps,
  deconstructForPreviewSetter,
} from 'components/features/preview-share/useChartRenderer';
import HighchartsReact from 'highcharts-react-official';
import { arrangeDateForChart } from 'components/features/utils/util';
import Highcharts, { merge } from 'highcharts';
import { renderToStaticMarkup } from 'react-dom/server';
import { AreaChartLegend } from 'components/features/new-report/components/AssetTransitionChart/HighChart';
import {
  LineCircleChartLegend,
  marker,
} from 'components/features/utils/chartUtils';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import svgToDataURL from 'svg-to-dataurl';

export type TopixDataProps = {
  topixData: TopixData[];
};

export const rangeSelectorButtonAlignLeftChartClassName =
  'align-left-range-selector-buttons';

export type IconOnChart = {
  icon: Highcharts.SVGElement;
};

const dataToSeries = (
  data: AssetStuffingChartDatum[],
  isTwoWeeks: boolean,
  topixData: TopixData[]
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      type: 'line',
      name: '資産',
      data: data
        .sort((a, b) => a.x.getTime() - b.x.getTime())
        .map(
          (datum): Highcharts.PointOptionsObject => {
            return {
              x: arrangeDateForChart(datum.x).getTime(),
              y: datum.y,
            };
          }
        ),
      color: '#43b5f7',
      marker: {
        enabled: isTwoWeeks,
        symbol: 'circle',
        radius: 4,
        lineWidth: 3,
        lineColor: '#43b5f7',
        fillColor: '#ffffff',
      },
      yAxis: 0,
      zIndex: 5,
    },
    {
      type: 'area',
      name: 'TOPIX',
      stacking: 'normal',
      data: topixData
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(
          (datum): Highcharts.PointOptionsObject => {
            return {
              x: arrangeDateForChart(datum.date).getTime(),
              y: datum.topix,
            };
          }
        ),
      turboThreshold: 15000,
      color: 'rgba(148, 163, 184, 0.7)',
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 0.25 },
        stops: [
          [0, 'rgba(148, 163, 184, 0.7)'],
          [0.1, 'rgba(148, 163, 184, 0.7)'],
          [0.2, 'rgba(148, 163, 184, 0.6)'],
          [0.3, 'rgba(148, 163, 184, 0.4)'],
          [0.4, 'rgba(148, 163, 184, 0.4)'],
          [0.5, 'rgba(148, 163, 184, 0.3)'],
          [0.6, 'rgba(148, 163, 184, 0.3)'],
          [0.7, 'rgba(148, 163, 184, 0.3)'],
          [0.8, 'rgba(148, 163, 184, 0.2)'],
          [0.9, 'rgba(148, 163, 184, 0.2)'],
          [1, 'rgba(148, 163, 184, 0.1)'],
          [1.1, 'rgba(148, 163, 184, 0.1)'],
        ],
      },
      marker: {
        enabled: false,
      },
      yAxis: 1,
      zIndex: 1,
    },
  ];
};

export const AssetStuffingChartInternal = ({
  data,
  options: options_,
  forPreviewSetter,
  extraData,
  topixData,
}: AssetStuffingChartProps &
  ChartForPreviewInternalProps &
  TopixDataProps): React.ReactElement => {
  const { setChartDataURL, setRange } = deconstructForPreviewSetter(
    forPreviewSetter
  );
  const [isTwoWeeks, setIsTwoWeeks] = useState<boolean>(true);
  const series = dataToSeries(data, isTwoWeeks, topixData);
  const [options, setOptions] = useState<Highcharts.Options>({
    credits: {
      enabled: false,
    },
    exporting: { enabled: false },
    rangeSelector: {
      dropdown: 'always',
      x: 20,
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 6,
        style: {
          textAlign: 'center',
          color: '#8e9db2',
        },
        states: {
          hover: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
          select: {
            fill: '#1E293B',
            style: {
              color: 'white',
              fontWeight: 'normal',
            },
          },
        },
      },
      inputEnabled: false,
      buttons: [
        {
          type: 'week',
          count: 2,
          text: '14日間',
          title: '14日間',
        },
        {
          type: 'month',
          count: 1,
          text: '1ヶ月',
          title: '1ヶ月',
        },
        {
          type: 'month',
          count: 3,
          text: '3ヶ月',
          title: '3ヶ月',
        },
        {
          type: 'month',
          count: 6,
          text: '6ヶ月',
          title: '6ヶ月',
        },
        {
          type: 'year',
          count: 1,
          text: '1年',
          title: '1年',
        },
        {
          type: 'all',
          text: '全期間',
          title: '全期間',
        },
      ],
      selected: forPreviewSetter.tag === 'forGetRange' ? 0 : undefined,
      buttonPosition: { align: 'left' },
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      itemStyle: { fontSize: '10px', fontWeight: 'normal', color: '#ffffff' },
      itemHoverStyle: { color: '#ffffff' },
      useHTML: true,
      itemDistance: -10,
      x: -5,
      y: 0,
      className: 'override-highcharts-legend',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = '#39a9f5';
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={'#94A3B8'} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineCircleChartLegend color={color}>
                {name}
              </LineCircleChartLegend>
            );
          }
        }
        return '';
      },
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          const label =
            name === '資産'
              ? `${commaFormat(value / 10000, { numberOfDecimals: 1 })}万円`
              : `${commaFormat(value, { numberOfDecimals: 2 })}`;
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {label}
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ?? []).map((point, key) => (
              <TooltipItem
                name={point.series.name}
                color={point.color.toString()}
                value={point.y}
                key={key}
              />
            ))}
          </Tooltip>
        );
      },
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      gridLineWidth: 0,
      dateTimeLabelFormats: {
        year: '%Y',
        month: '%Y/%m',
        day: '%m/%d',
        week: '%m/%d',
        hour: '%m/%d',
        minute: '%m/%d',
        second: '%m/%d',
        millisecond: '%m/%d',
      },
      events: {
        afterSetExtremes: function(event) {
          if (setRange) {
            setRange({
              min: new Date(event.min),
              max: new Date(event.max),
            });
          }
          setIsTwoWeeks((event.max - event.min) / 1000 / 60 / 60 / 24 <= 14);
        },
      },
      title: { style: { color: '#ffffff' } },
      labels: { style: { color: '#ffffff' } },
      tickColor: '#64748B',
      lineColor: '#64748B',
    },
    yAxis: [
      {
        labels: {
          formatter: function(e): string {
            return typeof this.value === 'number'
              ? commaFormat(this.value / 10000, { numberOfDecimals: 0 })
              : '';
          },
          align: 'left',
          x: 3,
          y: 4,
          style: { color: '#ffffff' },
        },
        title: {
          text: '(万円)',
          align: 'high',
          offset: 0,
          rotation: 0,
          y: 5,
          style: { color: '#ffffff' },
        },
        gridLineColor: '#64748B',
        opposite: true,
        alignTicks: false,
      },
      {
        labels: {
          enabled: false,
        },
        title: {
          text: null,
        },
        gridLineWidth: 0,
        opposite: true,
        tickInterval: 10,
        alignTicks: false,
      },
    ],
    chart: {
      className: rangeSelectorButtonAlignLeftChartClassName,
      animation: {
        duration: 0,
      },
      height: 280,
      marginBottom: 20,
      backgroundColor: '#475569',
      events: {
        redraw: function() {
          if (ref.current) {
            const chart = ref.current.chart;
            drawIcons({ chart, icons });
          }
        },
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: (): false => false,
        },
        threshold: null,
        turboThreshold: 15000,
        ...marker,
      },
    },
    series: series,
  });
  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);

  useEffect(() => {
    setOptions(prev => {
      return merge(
        {
          ...prev,
          series,
          rangeSelector: {
            buttonTheme: {
              selected:
                isTwoWeeks || forPreviewSetter.tag === 'forGetRange'
                  ? 0
                  : undefined,
            },
          },
        },
        options_ ?? {}
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options_, data, isTwoWeeks]);
  useEffect(() => {
    setChartDataURL &&
      ref.current &&
      setChartDataURL(svgToDataURL(ref.current.chart.getSVG()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const drawIcons = ({
    chart,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });
    const xAxis = chart.xAxis[0];

    const incorrect = extraData?.find(it => it.type === 'IncorrectSign');
    if (!incorrect) return;
    const signSeries = incorrect.value
      .filter(it => it.date && it.value)
      .map(it => it.date);

    signSeries.forEach(date => {
      const x = xAxis.toPixels(arrangeDateForChart(date).getTime(), true);
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();
      icon.toFront();
      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <div className="relative">
      <HighchartsReact
        highcharts={Highcharts}
        constructorType="stockChart"
        options={options}
        ref={ref}
      />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

export const AssetStuffingChart = ({
  setRange,
  ...props
}: AssetStuffingChartProps &
  ChartForNormalProps &
  TopixDataProps): React.ReactElement | null => {
  return (
    <>
      <AssetStuffingChartInternal
        {...{ ...props, forPreviewSetter: { tag: 'forGetRange', setRange } }}
      />
    </>
  );
};
